import { useState } from 'react';

import { DoNotDisturb, MailOutline, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    removeBranchDescendants,
    resetConditionalSplitSend,
    setSplitAction,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { findAncestorDoesNotMeetIndex, getEffectiveSplitType } from 'domains/campaigns/components/CustomCampaign/utils';
import { CampaignChannels, SplitActions, SplitTypes } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

import DisabledTooltipContent from '../../../components/DisabledTooltipContent';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseSplitAction = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const activeSend = campaignState.sends[activeSendIndex];
    const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;
    const effectiveSplitType = getEffectiveSplitType(activeSend as any, campaignState.sends);
    const isMeetsCriteriaSplit = isActiveSendEmail && effectiveSplitType === SplitTypes.MEETS;
    const isDoesNotMeetCriteriaSplit = isActiveSendEmail && effectiveSplitType === SplitTypes.DOES_NOT_MEET;
    const isRandomGroupA = isActiveSendEmail && effectiveSplitType === SplitTypes.RANDOM_GROUP_1;
    const isRandomGroupB = isActiveSendEmail && effectiveSplitType === SplitTypes.RANDOM_GROUP_2;

    // Check if editing a paused campaign
    const isEditingPausedCampaign = status === CampaignStatus.PAUSED;
    // Get existing split action for the current send
    const existingSplitAction = isActiveSendEmail ? (activeSend as any)?.splitAction : null;

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const action = (event.target as HTMLInputElement).value;

        if (action === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
            dispatchCampaignAction(removeBranchDescendants(activeSendIndex));
            dispatchCampaignAction(resetConditionalSplitSend(activeSendIndex));
            dispatchCampaignAction(setSplitAction(activeSendIndex, SplitActions.EXIT));

            if (isRandomGroupA) {
                const parentIndex = (activeSend as any).parentIndex ?? 0;
                const groupBIndex = campaignState.sends.findIndex(
                    (send: any) => send.splitType === SplitTypes.RANDOM_GROUP_2 && send.parentIndex === parentIndex
                );
                if (groupBIndex !== -1) {
                    setActiveSendIndex(groupBIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }
            } else if (isRandomGroupB) {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
            } else if (isMeetsCriteriaSplit) {
                setActiveSendIndex(activeSendIndex + 1);
            } else if (isDoesNotMeetCriteriaSplit) {
                const ancestorDnmIndex = findAncestorDoesNotMeetIndex(activeSend as any, campaignState.sends);
                if (ancestorDnmIndex !== -1) {
                    setActiveSendIndex(ancestorDnmIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                } else {
                    setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
                }
            }
            return;
        }

        if (action === CustomCampaignSteps.SELECT_CONTENT) {
            setSelectedAction(action);
            dispatchCampaignAction(setSplitAction(activeSendIndex, SplitActions.SEND));
            setActiveStep(action);
        }
    };

    // Determine which options should be disabled for paused campaigns
    const getIsOptionDisabled = (stepValue: CustomCampaignSteps) => {
        if (!isEditingPausedCampaign || !existingSplitAction) {
            return false;
        }
        // If email was selected (SEND), disable exit option
        if (existingSplitAction === SplitActions.SEND && stepValue === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
            return true;
        }
        // If exit was selected (EXIT), disable email option
        if (existingSplitAction === SplitActions.EXIT && stepValue === CustomCampaignSteps.SELECT_CONTENT) {
            return true;
        }
        return false;
    };

    const steps = [
        {
            disabled: getIsOptionDisabled(CustomCampaignSteps.SELECT_CONTENT),
            icon: <MailOutline sx={iconStyles} />,
            label: 'email',
            value: CustomCampaignSteps.SELECT_CONTENT,
        },
        {
            disabled: getIsOptionDisabled(CustomCampaignSteps.PUBLISH_CAMPAIGN),
            icon: <DoNotDisturb sx={iconStyles} />,
            label: 'exit curation',
            value: CustomCampaignSteps.PUBLISH_CAMPAIGN,
        },
    ];

    const guidingText = isRandomGroupA
        ? 'Choose the next step for Group 1 (50%). Select an email or exit the curation for this group.'
        : isRandomGroupB
        ? 'Choose the next step for Group 2 (50%). Select an email or exit the curation for this group.'
        : `Choose the next step for your audience who ${
              isDoesNotMeetCriteriaSplit ? 'does not meet' : 'meets'
          } the split criteria. Keeping your Curation goals in mind, select the action that supports your objective.`;

    return (
        <FormLayout guidingText={guidingText} header="select path actions" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleStepClick} value={selectedAction}>
                {steps.map((step) => (
                    <DisabledTooltipContent key={step.value} disabled={step.disabled}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{
                                ...sharedButtonStyles,
                                ...(step.disabled && disabledButtonStyles),
                            }}
                            value={step.value}
                        />
                    </DisabledTooltipContent>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseSplitAction;
